import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check } from "lucide-react";

// A filter list longer than this gets a search box — and is too long to lay
// out as chips, so the mobile filter bar gives it a dropdown instead.
export const SEARCHABLE_FROM = 8;

/**
 * A column filter is described once per page and rendered twice: as a
 * TableColumnHeader dropdown on desktop, as a chip row or chip dropdown in the
 * MobileFilterBar below `lg`. The descriptor is the shared contract:
 *
 *   { value, onChange, options, multiple, active, clearLabel, align }
 *
 * Single-select: `value` is a string, picking closes the dropdown, and the
 * empty-string option value means "no filter". Multi-select (`multiple`):
 * `value` is an array and picking toggles. Pass `active` when "filtered" is not
 * simply "something is selected" (e.g. a default selection that should still
 * read as unfiltered), and `clearLabel` to rename the clear action.
 */
export function isFilterActive(filter) {
  if (filter.active !== undefined) return filter.active;
  return filter.multiple ? (filter.value || []).length > 0 : Boolean(filter.value);
}

export function isOptionActive(filter, value) {
  return filter.multiple ? (filter.value || []).includes(value) : filter.value === value;
}

export function toggleOption(filter, value) {
  const selected = filter.value || [];
  filter.onChange(
    selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]
  );
}

/**
 * The dropdown body: search box, option rows, and the multi-select footer.
 * Portalled to `document.body` and placed under `anchorRef` (a table header
 * cell, a mobile chip), because inside a header cell the table's `overflow-auto`
 * wrapper clipped it to the table's own height — half the options hidden when
 * a filter left one or two rows (#553). The caller owns the open state; the
 * panel closes itself on a mousedown outside both the anchor and itself.
 */
export function FilterPanel({ filter, label, onClose, anchorRef }) {
  const [search, setSearch] = useState("");
  const panelRef = useRef(null);

  // Written straight to the node rather than kept in state: the panel is in place
  // before first paint (no hidden frame that would swallow focus), and scrolling
  // re-renders nothing. Document coordinates, so the page scrolling carries the
  // panel with it and a tall list lengthens the page.
  useLayoutEffect(() => {
    const panel = panelRef.current;
    const place = () => {
      const anchor = anchorRef.current;
      // A display:none anchor (the mobile bar once the window crosses `lg`) has no
      // box; without this the panel would jump to the page's top-left corner.
      panel.style.visibility = anchor?.getClientRects().length ? "" : "hidden";
      if (!anchor) return;
      const r = anchor.getBoundingClientRect();
      const w = panel.offsetWidth;
      // Kept on screen: out here nothing clips it any more, so a chip near a phone's
      // right edge (or a scrolled-away column) would otherwise widen the page.
      const want = filter.align === "right" ? r.right - w : r.left;
      const x = Math.max(8, Math.min(want, document.documentElement.clientWidth - w - 8));
      panel.style.top = `${r.bottom + window.scrollY}px`;
      panel.style.left = `${x + window.scrollX}px`;
    };
    place();
    // Capture: an inner container (the table wrapper, `main` at `lg`) scrolling moves the anchor too.
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [anchorRef, filter.align]);

  // Once placed. The portal puts the panel at the end of <body>, so Tab from the
  // trigger no longer reaches it; and `autoFocus` would fire before placement,
  // scrolling the page to wherever the unplaced panel sits.
  useLayoutEffect(() => {
    panelRef.current.querySelector("input, button")?.focus();
  }, []);

  useEffect(() => {
    const onDocMouseDown = (e) => {
      if (!anchorRef.current?.contains(e.target) && !panelRef.current?.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [anchorRef, onClose]);

  const term = search.trim().toLowerCase();
  const options = term
    ? filter.options.filter((o) => o.label.toLowerCase().includes(term))
    : filter.options;

  const pick = (value) => {
    if (!filter.multiple) {
      filter.onChange(value);
      onClose();
      return;
    }
    toggleOption(filter, value);
  };

  return createPortal(
    <div
      ref={panelRef}
      className="absolute z-50 mt-1 bg-background border border-input rounded-md shadow-lg py-1 w-max min-w-[200px] max-w-[min(280px,calc(100vw-2rem))]"
    >
      {filter.options.length > SEARCHABLE_FROM && (
        <div className="px-2 py-1.5">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={`Search ${label.toLowerCase()}...`}
            className="w-full h-7 rounded border border-input bg-background px-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
      )}
      <div className="max-h-56 overflow-y-auto">
        {options.length === 0 ? (
          <p className="px-3 py-2 text-sm text-muted-foreground">No matches</p>
        ) : (
          options.map((opt) => {
            const active = isOptionActive(filter, opt.value);
            return (
              <button
                key={opt.value || "__all__"}
                type="button"
                onClick={() => pick(opt.value)}
                className={`w-full text-left px-3 py-2.5 sm:py-1.5 text-sm flex items-center gap-2 hover:bg-muted/50 ${
                  active ? "text-primary font-medium" : ""
                }`}
              >
                <span
                  className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${
                    active ? "bg-primary border-primary" : "border-input"
                  }`}
                >
                  {active && <Check className="h-3 w-3 text-primary-foreground" />}
                </span>
                <span className="truncate">{opt.label}</span>
              </button>
            );
          })
        )}
      </div>
      {filter.multiple && (
        <div className="border-t mt-1 pt-1 flex text-sm text-muted-foreground">
          <button
            type="button"
            // Additive: a search narrows what is listed, it must not drop
            // selections that are currently filtered out of view.
            onClick={() =>
              filter.onChange([
                ...new Set([...(filter.value || []), ...options.map((o) => o.value)]),
              ])
            }
            className="flex-1 text-left px-3 py-1.5 hover:bg-muted/50"
          >
            Select all
          </button>
          <button
            type="button"
            onClick={() => filter.onChange([])}
            className="flex-1 text-right px-3 py-1.5 hover:bg-muted/50"
          >
            {filter.clearLabel || "Clear"}
          </button>
        </div>
      )}
    </div>,
    document.body
  );
}
